import React from "react";
import { Loading } from "@/components/LoadingState";
import { Edit, FileText, Trash2, ExternalLink, MoreVertical } from "lucide-react";
import { formatNum, formatTL, fmtDate } from "@/lib/api";
import { PrimMonthBadge } from "./PrimMonth";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function PrimMobileCards({
  rows,
  totals,
  loading,
  canSeePrimRate,
  isPersonel,
  onEdit,
  onDelete,
  onPdf,
}) {
  const { isAdmin } = useAuth();
  return (
    <div className="md:hidden divide-y divide-slate-100" data-testid="prim-cards">
      {loading && (
        <Loading text="Primler yükleniyor..." />
      )}
      {!loading && rows.length === 0 && (
        <div className="py-10 text-center text-slate-400 text-sm">
          Prim kaydı bulunamadı. Sağ üstten ekleyebilirsiniz.
        </div>
      )}
      {rows.map((r) => {
        const g = r.giden_isimleri || [];
        return (
          <div key={r.id} data-testid={`prim-card-${r.id}`} className="p-4 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-slate-900 truncate">{r.dosya_adi || "—"}</div>
                {r.varlik_sirketi && (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">Varlık</span>
                )}
                {r.muhasebeci_onay && (
                  <span
                    data-testid={`prim-card-preapproved-${r.id}`}
                    className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-300"
                    title={`Muhasebeci ön onayı · ${r.muhasebeci_onay.by_email || "?"}`}
                  >
                    ✓ Ön Onay
                  </span>
                )}
                {r.onay_override && (
                  <span
                    data-testid={`prim-card-override-${r.id}`}
                    className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800 border border-red-300"
                    title={`Admin OVERRIDE · ${r.onay_override_blockers?.length || 0} mükerrer föy bypass`}
                  >
                    ⚠ Override
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{r.alacakli_adi || "—"}</div>
              {Array.isArray(r.tags) && r.tags.length > 0 && (
                <div className="flex flex-wrap gap-0.5 mt-1" data-testid={`prim-card-tags-${r.id}`}>
                  {r.tags.slice(0, 4).map((t) => (
                    <span key={t} className="inline-flex items-center text-[9px] font-semibold px-1.5 py-0 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                      # {t}
                    </span>
                  ))}
                  {r.tags.length > 4 && (
                    <span className="text-[9px] text-slate-400">+{r.tags.length - 4}</span>
                  )}
                </div>
              )}
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="text-slate-400">Tarih: </span>
                  <span className="font-mono-tab text-slate-700">{fmtDate(r.tarih)}<PrimMonthBadge r={r} /></span>
                </div>
                {canSeePrimRate && (
                  <div>
                    <span className="text-slate-400">Oran: </span>
                    <span className="font-mono-tab text-slate-700">%{formatNum(r.oran)}</span>
                  </div>
                )}
                <div>
                  <span className="text-slate-400">Föy: </span>
                  <span className="font-mono-tab text-slate-700">{r.icra_foy || "—"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400">Esas: </span>
                  <span className="font-mono-tab text-slate-700">{r.icra_esas || "—"}</span>
                  {r.uyap_url && (
                    <a
                      href={r.uyap_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-blue-50 text-blue-700"
                      title="UYAP'ta Aç"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                <div>
                  <span className="text-slate-400">Vekalet: </span>
                  <span className="font-mono-tab text-slate-700">{formatNum(r.vekalet_ucreti)}</span>
                </div>
                <div>
                  <span className="text-slate-400">Tahsilat: </span>
                  <span className="font-mono-tab text-slate-700">{formatNum(r.tahsilat_miktari)}</span>
                </div>
                <div className="col-span-2 pt-1 border-t border-slate-100">
                  <span className="text-slate-400">Ödenecek: </span>
                  <span className="font-mono-tab font-bold text-emerald-700">
                    {canSeePrimRate ? formatTL(r.odenecek_toplam) : "•••••"}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-400">Kişi Başı: </span>
                  <span className="font-mono-tab font-semibold text-emerald-700">
                    {canSeePrimRate ? formatTL(r.kisi_basi_prim) : "•••••"}
                  </span>
                  <span className="text-slate-400"> · {g.filter(Boolean).length} kişi</span>
                </div>
                {g.filter(Boolean).length > 0 && (
                  <div className="col-span-2 text-slate-600 truncate">
                    <span className="text-slate-400">Giden: </span>{g.filter(Boolean).join(", ")}
                  </div>
                )}
                {r.arastiran_isim && !isPersonel && (
                  <div className="col-span-2">
                    <span className="text-slate-400">Araştıran: </span>
                    <span className="text-slate-700">{r.arastiran_isim}</span>
                    {canSeePrimRate && (
                      <span className="font-mono-tab text-indigo-700 font-semibold"> · {formatTL(r.arastirma_primi)}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`prim-actions-${r.id}`}
                  className="shrink-0 w-9 h-9 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
                >
                  <MoreVertical size={18} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {!isPersonel && (
                  <DropdownMenuItem onSelect={() => onPdf(r)} data-testid={`m-pdf-prim-${r.id}`}>
                    <FileText size={14} className="mr-2 text-rose-600" /> PDF Makbuz
                  </DropdownMenuItem>
                )}
                {(isAdmin || !r.onaylandi) && (
                  <DropdownMenuItem onSelect={() => onEdit(r)} data-testid={`m-edit-prim-${r.id}`}>
                    <Edit size={14} className="mr-2 text-slate-600" /> Düzenle
                  </DropdownMenuItem>
                )}
                {(isAdmin || !r.onaylandi) && (
                  <DropdownMenuItem
                    onSelect={() => onDelete(r.id)}
                    className="text-rose-600 focus:text-rose-700"
                    data-testid={`m-del-prim-${r.id}`}
                  >
                    <Trash2 size={14} className="mr-2" /> Sil
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })}
      {rows.length > 0 && canSeePrimRate && (
        <div className="p-4 bg-slate-50 border-t border-slate-200" data-testid="prim-cards-totals">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Toplam ({rows.length} kayıt)
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <div><span className="text-slate-400">Vekalet: </span><span className="font-mono-tab font-semibold">{formatNum(totals.vekalet)}</span></div>
            <div><span className="text-slate-400">Tahsilat: </span><span className="font-mono-tab font-semibold">{formatNum(totals.tahsilat)}</span></div>
            <div><span className="text-slate-400">Ödenecek: </span><span className="font-mono-tab font-bold text-emerald-700">{formatNum(totals.odenecek)}</span></div>
            <div><span className="text-slate-400">Kişi Başı: </span><span className="font-mono-tab font-bold text-emerald-700">{formatNum(totals.kisi_basi)}</span></div>
            <div className="col-span-2"><span className="text-slate-400">Araştırma Primi: </span><span className="font-mono-tab font-bold text-indigo-700">{formatNum(totals.arastirma_primi)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
